import { PATIENTS_LIMIT } from "constants/dashboard";
import { CommonApiParams, FILTER_KEYS, FilterParams } from "types/common";
import { changeSort } from "utils/general";
import { ExamRepository } from "repositories/ExamRepository";
import { PatientRegionsRepository } from "repositories/PatientRegionsRepository";
import { Movement } from "types/Region";
import { convertFilterParamsToQueryObject } from "utils/formatters";
import { PatientRepository } from "repositories/PatientRepository";
import {
  setCurrentPatient,
  setExamList,
  setJointFilterParams,
  setLimit,
  setLoading,
  setPatientSportsUploading,
  setOffset,
  setPatientRegionDetails,
  cleanCurrentPatientInfo,
  setSortConfig,
  setTotalExams,
  setTypeFilterParams,
  setAthleteIdInProcess,
  setSportFilterCategories,
  setClinicPatientsList,
  setTotalClinicPatients,
  setSortConfigClinicPatients,
  setOffsetClinicPatients,
  setLimitClinicPatients,
  setCurrentPatientDailyCheckinData,
  setCurrentPatientDailyCheckinMonthData,
  setCurrentPatientDailyCheckinWeekData,
  setCurrentPatientDailyCheckinYearData,
  setJointOptions,
} from "./patientSlice";
import { AppThunk } from "../rootStore";
import { addRequest, removeRequest } from "../LoadingStore/loadingSlice";
import { Requests } from "../../types/requests";
import { initPatientRegionsPage } from "store/PatientRegionsStore/actions";

const FULL_LIST = 999999999;
export const initPatientDetailsPage =
  (patientId: string): AppThunk =>
  async (dispatch) => {
    try {
      const isFreeForm = window.location.search.includes("freeForm=true");
      dispatch(getPatient(patientId));
      dispatch(getPatientRegionDetails(patientId));
      dispatch(getExamList(patientId, true, isFreeForm));
      dispatch(initJointFilters());
      dispatch(initTypeFilters());
      dispatch(setTotalExams(0));
      dispatch(setOffset(0));
    } catch (error) {
      console.log(error);
    }
  };

export const cleanUpCurrentPatientInfo = (): AppThunk => async (dispatch) => {
  try {
    dispatch(cleanCurrentPatientInfo());
  } catch (error) {
    console.log(error);
  }
};

export const getPatient =
  (patientId: string): AppThunk =>
  async (dispatch) => {
    try {
      const patientRepository = PatientRepository.getInstance();
      dispatch(addRequest(Requests.getPatient));

      const patient = await patientRepository.getPatient(patientId);
      dispatch(setCurrentPatient(patient));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(removeRequest(Requests.getPatient));
    }
  };

export const getClinicPatientsList: any =
  (callback?: () => void, authStatusFilters?: any): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getClinicPatientsList));

      const {
        patient: {
          limitClinicPatients,
          offsetClinicPatients,
          sortConfigClinicPatients,
          jointFilterParams,
          search,
        },
      } = getState();

      const filterParamsQueryObject =
        convertFilterParamsToQueryObject(jointFilterParams);

      const apiParams: CommonApiParams = {
        ...filterParamsQueryObject,
        ...(sortConfigClinicPatients.sortBy !== "" && {
          sortBy: sortConfigClinicPatients.sortBy,
          sortDirection: sortConfigClinicPatients.sortDirection,
        }),
        limit: limitClinicPatients,
        offset: offsetClinicPatients,
      };
      const patientRepository = PatientRepository.getInstance();
      const patientsList = await patientRepository.getPatientsForCurrentClinic({
        ...apiParams,
        search,
        ...authStatusFilters,
      });
      dispatch(setClinicPatientsList([]));
      dispatch(setClinicPatientsList(patientsList?.items));
      dispatch(setTotalClinicPatients(patientsList?.totalCount));
      dispatch(setLimitClinicPatients(PATIENTS_LIMIT));
    } catch (error) {
      console.log("Error from getClinicPatientsList Thunk", error);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getMe));

      if (callback) callback();
    }
  };

export const goToPageClinicPatients =
  (page: number): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const patientRepository = PatientRepository.getInstance();
      const {
        patient: {
          limitClinicPatients,
          sortConfigClinicPatients,
          search,
        },
      } = getState();
      dispatch(addRequest(Requests.loadMoreClinicPatients));
      const newOffset = (page - 1) * limitClinicPatients;
      const apiParams: CommonApiParams = {
        ...(sortConfigClinicPatients.sortBy !== "" && {
          sortBy: sortConfigClinicPatients.sortBy,
          sortDirection: sortConfigClinicPatients.sortDirection,
        }),
        limit: limitClinicPatients,
        offset: newOffset,
      };

      const patients = await patientRepository.getPatientsForCurrentClinic({
        ...apiParams,
        search,
      });
      dispatch(setClinicPatientsList(patients.items));
      dispatch(setTotalClinicPatients(patients.totalCount));
      dispatch(setOffsetClinicPatients(newOffset));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.loadMoreClinicPatients));
    }
  };

/** EXAM LIST */
export const getExamList =
  (patientId: string, fullList?: boolean, freeForm?: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const examRepository = ExamRepository.getInstance();
      const {
        patient: { limit, sortConfig, jointFilterParams, typeFilterParams },
      } = getState();
      dispatch(addRequest(Requests.getExamList));

      const filterParamsQueryObject = convertFilterParamsToQueryObject([
        ...jointFilterParams,
        ...typeFilterParams,
      ]);

      const apiParams: CommonApiParams & { freeForm?: boolean } = {
        ...(sortConfig.sortBy !== "" && {
          sortBy: sortConfig.sortBy,
          sortDirection: sortConfig.sortDirection,
        }),
        ...filterParamsQueryObject,
        limit: fullList ? FULL_LIST : limit,
        offset: 0,
        freeForm,
      };

      const response = await examRepository.getExams(patientId, apiParams);
      const type = freeForm ? ["freeForm"] : ["dynamic", "rom"];
      const responseItems = response.items.filter(
        (item) => type.includes(item.examType) || !item.examType,
      );

      const responseTotalCount = response.totalCount;
      dispatch(setExamList(responseItems));
      dispatch(setTotalExams(responseTotalCount));

      // need to reset because of sorting flow
      dispatch(setLimit(PATIENTS_LIMIT));
    } catch (error) {
      console.log(error);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getPatientRegionDetails));
    }
  };

export const changeSortOfExamList =
  (sortBy: string, patientId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      const {
        patient: { sortConfig },
      } = getState();

      const isFreeForm = window.location.search.includes("freeForm=true");
      const changedSortConfig = changeSort(sortBy, sortConfig);
      // to save quantity of existing elements
      // dispatch(setLimit(limit + offset));

      dispatch(setSortConfig(changedSortConfig));
      dispatch(setOffset(0));

      dispatch(getExamList(patientId, true, isFreeForm));
    } catch (error) {
      console.log(error);
    }
  };

export const changeSortOfClinicPatientsList =
  (sortBy: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      const {
        patient: { sortConfigClinicPatients },
      } = getState();

      const changedSortConfig = changeSort(sortBy, sortConfigClinicPatients);
      dispatch(setSortConfigClinicPatients(changedSortConfig));
    } catch (error) {
      console.log(error);
    }
  };

export const loadMoreExams = (): AppThunk => async (dispatch, getState) => {
  try {
    dispatch(setLoading(true));
    const examRepository = ExamRepository.getInstance();
    const {
      patient: {
        limit,
        totalExams,
        offset,
        currentPatient,
        sortConfig,
        examList,
        jointFilterParams,
        typeFilterParams,
      },
    } = getState();
    if (totalExams <= offset + limit) {
      dispatch(setLoading(false));
      return;
    }
    dispatch(addRequest(Requests.loadMoreExams));

    const newOffset = offset + limit;
    const filterParamsQueryObject = convertFilterParamsToQueryObject([
      ...jointFilterParams,
      ...typeFilterParams,
    ]);

    const isFreeForm = window.location.search.includes("freeForm=true");
    const apiParams: CommonApiParams & { freeForm?: boolean } = {
      ...(sortConfig.sortBy !== "" && {
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      }),
      ...filterParamsQueryObject,
      limit,
      offset: newOffset,
      freeForm: isFreeForm,
    };

    const exams = await examRepository.getExams(currentPatient?._id, apiParams);
    const type = isFreeForm ? "freeForm" : "dynamic";
    const responseItems = exams.items.filter(
      (item) => item.examType === type || !item.examType,
    );
    dispatch(setExamList([...examList, ...responseItems]));
    dispatch(setOffset(newOffset));
  } catch (error) {
    console.log(error);
    dispatch(setLoading(false));
  } finally {
    dispatch(setLoading(false));
    dispatch(removeRequest(Requests.loadMoreExams));
  }
};

export const getPatientRegionDetails =
  (patientId: string): AppThunk =>
  async (dispatch) => {
    try {
      const patientRegionsRepository = PatientRegionsRepository.getInstance();
      dispatch(addRequest(Requests.getPatientRegionDetails));

      const patientRegionDetails =
        await patientRegionsRepository.getPatientRegionDetails(patientId);
      dispatch(setPatientRegionDetails(patientRegionDetails));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(removeRequest(Requests.getPatientRegionDetails));
    }
  };

export const initJointFilters = (): AppThunk => async (dispatch) => {
  try {
    const patientRegionsRepository = PatientRegionsRepository.getInstance();
    const joints = await patientRegionsRepository.getJoints();

    const jointFilterParams: FilterParams[] = joints.map((joint: any) => ({
      id: joint._id,
      title: `${joint.side} ${joint.name}`,
      checked: false,
      filterKey: FILTER_KEYS.joint,
    }));

    const jointOptions = joints.map((joint) => ({
      value: joint._id,
      label: `${joint.side[0].toUpperCase()}${joint.side.slice(
        1,
      )} ${joint.name[0].toUpperCase()}${joint.name.slice(1)}`,
    }));

    dispatch(setJointFilterParams(jointFilterParams));
    dispatch(setJointOptions(jointOptions));
  } catch (error) {
    console.log(error);
  }
};

export const initTypeFilters = (): AppThunk => async (dispatch) => {
  try {
    const types: Movement[] = [{ _id: "id", name: "ROM" }];

    const jointFilterParams: FilterParams[] = types.map((type) => ({
      id: type._id,
      title: type.name,
      checked: false,
      filterKey: FILTER_KEYS.type,
    }));

    dispatch(setTypeFilterParams(jointFilterParams));
  } catch (error) {
    console.log(error);
  }
};

export const changeFilterParams =
  (filterKey: FILTER_KEYS, filterParams: FilterParams[]): AppThunk =>
  async (dispatch, getState) => {
    const {
      patient: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        currentPatient: { _id },
      },
    } = getState();
    try {
      switch (filterKey) {
        case FILTER_KEYS.joint:
          dispatch(setJointFilterParams(filterParams));
          break;
        case FILTER_KEYS.type:
          dispatch(setTypeFilterParams(filterParams));
          break;
        default:
          dispatch(setJointFilterParams(filterParams));
      }
      dispatch(setOffset(0));

      dispatch(getExamList(_id));
    } catch (error) {
      console.log(error);
    }
  };

export const updatePatient =
  (patientId: string, patientSports: string[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setPatientSportsUploading(true));
      dispatch(setAthleteIdInProcess(patientId));
      const patientRepository = PatientRepository.getInstance();
      await patientRepository.updatePatient(patientId, {
        sports: patientSports,
      });
      dispatch(initPatientRegionsPage());
      dispatch(setPatientSportsUploading(false));
      dispatch(setAthleteIdInProcess(""));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(removeRequest(Requests.getPatient));
    }
  };

export const assignTeamMembersToPatient =
  (patientId: string, teamMembers: string[], callback?: () => void): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.assignTeamMembersToPatient));
      const patientRepository = PatientRepository.getInstance();
      await patientRepository.assignTeamMembers(patientId, {
        assignedTeamMembers: teamMembers,
      });
      dispatch(setLoading(false));
    } catch (error) {
      dispatch(setLoading(false));
      console.log(error);
    } finally {
      dispatch(removeRequest(Requests.assignTeamMembersToPatient));
      const {
        patient: { limitClinicPatients },
      } = getState();
      const lastLimit = limitClinicPatients - PATIENTS_LIMIT;
      if (callback) callback();
      dispatch(setOffsetClinicPatients(lastLimit));
      dispatch(setLimitClinicPatients(PATIENTS_LIMIT));
    }
  };

export const assignTeamLeadToPatient =
  (
    patientId: string,
    teamLead: string | null,
    callback?: () => void,
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.assignTeamLeadToPatient));
      const patientRepository = PatientRepository.getInstance();
      await patientRepository.assignTeamLead(patientId, {
        assignedTeamLead: teamLead,
      });
      dispatch(setLoading(false));
    } catch (error) {
      dispatch(setLoading(false));
      console.log(error);
    } finally {
      dispatch(removeRequest(Requests.assignTeamLeadToPatient));
      const {
        patient: { limitClinicPatients },
      } = getState();
      const lastLimit = limitClinicPatients - PATIENTS_LIMIT;
      if (callback) callback();
      dispatch(setOffsetClinicPatients(lastLimit));
      dispatch(setLimitClinicPatients(PATIENTS_LIMIT));
    }
  };

export const filterSportCategories =
  (filterSportCategories: string[]): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setSportFilterCategories(filterSportCategories));
    } catch (error) {
      console.log(error);
    }
  };

export const getPatientDailyCheckinData =
  (patientId: string, date?: Date): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const patientRepository = PatientRepository.getInstance();
      dispatch(addRequest(Requests.getPatientDailyCheckinData));

      const paramsMonth = {
        date: date,
        dateRangeType: "month" as const,
      };
      const paramsWeek = {
        date: date,
        dateRangeType: "week" as const,
      };
      const paramsYear = {
        date: date,
        dateRangeType: "year" as const,
      };

      const patientDailyCheckinMonthData =
        await patientRepository.getPatientDailyCheckinData(
          patientId,
          paramsMonth,
        );
      const patientDailyCheckinWeekData =
        await patientRepository.getPatientDailyCheckinData(
          patientId,
          paramsWeek,
        );
      const patientDailyCheckinYearData =
        await patientRepository.getPatientDailyCheckinData(
          patientId,
          paramsYear,
        );

      dispatch(setCurrentPatientDailyCheckinData(patientDailyCheckinMonthData));
      dispatch(
        setCurrentPatientDailyCheckinMonthData(patientDailyCheckinMonthData),
      );
      dispatch(
        setCurrentPatientDailyCheckinWeekData(patientDailyCheckinWeekData),
      );
      dispatch(
        setCurrentPatientDailyCheckinYearData(patientDailyCheckinYearData),
      );
      dispatch(setLoading(false));
    } catch (error) {
      console.log(error);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getPatientDailyCheckinData));
    }
  };
